/**
 * Create Batch Order Dialog Component
 * Dialog for creating new orders with batch selection from manufacturing inventory
 *
 * Features:
 * - Customer Selection
 * - Product/Batch Selection with real-time quantity tracking
 * - GST Calculation (CGST/SGST breakdown)
 * - Delivery Date and Address
 * - Order Stage initialization (INITIALIZED -> BATCH_ALLOCATED)
 *
 * @author SAUBHAGYA Development Team
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Loader2, AlertTriangle, ShoppingCart, UserPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { BatchSelector } from './BatchSelector';
import { useCustomers, type Customer } from '@/hooks/useCustomers';
import { createOrderWithBatches, type BatchSelection, type CreateOrderWithBatchRequest } from '@/services/commerce/batchService';
import { generateInvoiceForOrder } from '@/services/invoiceGenerationService';

interface CreateBatchOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOrderCreated?: () => void;
  defaultCustomerId?: number;
}

export function CreateBatchOrderDialog({
  open,
  onOpenChange,
  onOrderCreated,
  defaultCustomerId,
}: CreateBatchOrderDialogProps) {
  // State
  const [customerId, setCustomerId] = useState<number | null>(defaultCustomerId || null);
  const [batchSelections, setBatchSelections] = useState<BatchSelection[]>([]);
  const [orderTotal, setOrderTotal] = useState(0);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tracks the last customer whose address was auto-filled so we only auto-fill
  // once per customer selection and never fight the user's manual edits/clears.
  const autoFilledForCustomerIdRef = useRef<number | null>(null);

  // Hooks
  const { data: customers = [], isLoading: loadingCustomers, refetch: refetchCustomers } = useCustomers();
  const navigate = useNavigate();

  // Handle navigation to create new customer
  const handleCreateNewCustomer = () => {
    const returnUrl = encodeURIComponent('/sales/orders/new');
    navigate(`/sales/customers?returnTo=${returnUrl}&openForm=true`);
  };

  // Reset form when dialog opens (only run when dialog state changes)
  useEffect(() => {
    if (open) {
      console.log('📦 Order dialog opened with defaultCustomerId:', defaultCustomerId);
      setBatchSelections([]);
      setOrderTotal(0);
      setDeliveryDate('');
      setDeliveryAddress('');
      setSpecialInstructions('');
      setError(null);
      autoFilledForCustomerIdRef.current = null;

      // If defaultCustomerId is provided, refetch customers to ensure newly created customer is in the list
      if (defaultCustomerId) {
        console.log('🔄 Refetching customers to include newly created customer:', defaultCustomerId);
        refetchCustomers();
      }
    }
  }, [open, defaultCustomerId, refetchCustomers]);

  // Set default delivery date (10 working days from today)
  useEffect(() => {
    // Calculate 10 working days from today
    const today = new Date();
    let deliveryDateCalc = new Date(today);
    let workingDaysAdded = 0;

    while (workingDaysAdded < 10) {
      deliveryDateCalc.setDate(deliveryDateCalc.getDate() + 1);
      const dayOfWeek = deliveryDateCalc.getDay();
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        workingDaysAdded++;
      }
    }

    const deliveryDateString = deliveryDateCalc.toISOString().split('T')[0];
    setDeliveryDate(deliveryDateString);
  }, []);

  // Set customer ID when defaultCustomerId is provided (separate effect)
  useEffect(() => {
    if (open && defaultCustomerId) {
      console.log('🎯 Setting customer ID to:', defaultCustomerId);
      setCustomerId(defaultCustomerId);
    } else if (open && !defaultCustomerId) {
      console.log('🎯 No defaultCustomerId, resetting to null');
      setCustomerId(null);
    }
  }, [open, defaultCustomerId]);

  // Show toast notification when customer is found in the list
  useEffect(() => {
    if (open && defaultCustomerId && customers.length > 0) {
      const customer = customers.find((c) => c.id === defaultCustomerId);
      if (customer) {
        console.log('✅ Customer found for auto-selection:', customer.companyName || customer.contactPerson);
        toast.success(`Customer "${customer.companyName || customer.contactPerson}" auto-selected`, {
          description: 'You can now proceed to create the order.',
          duration: 4000,
        });
      } else {
        console.warn('⚠️ Customer ID', defaultCustomerId, 'not found in customers list');
        console.warn('⚠️ Available customer IDs:', customers.map(c => c.id));
        // Customer might be newly created and not yet in the list, wait for refetch
        toast.info('Loading customer details...', {
          duration: 2000,
        });
      }
    }
  }, [open, defaultCustomerId, customers]);

  // Debug: Log customer selection state
  useEffect(() => {
    if (open) {
      console.log('🔍 Customer Selection State:', {
        customerId,
        customerIdAsString: customerId?.toString(),
        defaultCustomerId,
        customersCount: customers.length,
        customerIds: customers.map(c => c.id),
        matchingCustomer: customers.find(c => c.id === customerId),
      });
    }
  }, [open, customerId, customers, defaultCustomerId]);

  // Auto-fill delivery address once per customer selection.
  // Why: previously the effect depended on `deliveryAddress` with an `!deliveryAddress`
  // guard, which re-populated the field whenever the user cleared it — making it
  // impossible to remove or change. Tracking the last auto-filled customer in a ref
  // lets the fill happen exactly once per customer change and preserves manual edits.
  useEffect(() => {
    if (!customerId) {
      autoFilledForCustomerIdRef.current = null;
      return;
    }
    if (autoFilledForCustomerIdRef.current === customerId) return;

    const customer = customers.find((c) => c.id === customerId);
    if (!customer) return; // wait until customer data is available

    setDeliveryAddress(customer.registeredAddress ?? '');
    autoFilledForCustomerIdRef.current = customerId;
  }, [customerId, customers]);

  const handleSelectionsChange = useCallback(
    (selections: BatchSelection[], total: number) => {
      setBatchSelections(selections);
      setOrderTotal(total);
    },
    []
  );

  const handleSubmit = async () => {
    // Validation
    if (!customerId) {
      setError('Please select a customer');
      return;
    }

    if (batchSelections.length === 0) {
      setError('Please add at least one batch selection');
      return;
    }

    if (!deliveryDate) {
      setError('Please select an expected delivery date');
      return;
    }

    if (!deliveryAddress.trim()) {
      setError('Please enter a delivery address');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const request: CreateOrderWithBatchRequest = {
        customerId,
        batchSelections,
        expectedDeliveryDate: deliveryDate,
        deliveryAddress: deliveryAddress.trim(),
        specialInstructions: specialInstructions.trim() || undefined,
      };

      const orderResponse = await createOrderWithBatches(request) as { id?: number; orderId?: number; order?: { id?: number } };

      // Extract order ID from response
      const orderId = orderResponse?.id || orderResponse?.orderId || orderResponse?.order?.id;

      toast.success('Order created successfully with batch allocation!');

      // Generate invoice for the created order
      if (orderId) {
        try {
          const invoiceResponse = await generateInvoiceForOrder(orderId);
          if (invoiceResponse) {
            toast.success(`Invoice ${invoiceResponse.invoiceNumber || ''} generated successfully!`);
          }
        } catch (invoiceError) {
          console.error('Invoice generation failed:', invoiceError);
          toast.warning('Order created but invoice generation failed. Please generate manually.');
        }
      } else {
        console.warn('Order created but could not extract order ID for invoice generation');
        toast.warning('Order created but could not generate invoice automatically.');
      }

      onOpenChange(false);
      onOrderCreated?.();
    } catch (err) {
      console.error('Error creating order:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to create order';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const cgst = orderTotal * 0.09;
  const sgst = orderTotal * 0.09;
  const finalAmount = orderTotal + cgst + sgst;

  // Get minimum delivery date (tomorrow)
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Create Order with Batch Allocation
          </DialogTitle>
          <DialogDescription>
            Select products and batches to create a new order with real-time inventory allocation
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Customer Selection */}
          <div className="space-y-2">
            <Label>Customer *</Label>
            {defaultCustomerId && customerId === defaultCustomerId && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                ✓ Customer auto-selected from inquiry
              </p>
            )}
            <div className="flex gap-2">
              <Select
                value={customerId?.toString() ?? ''}
                onValueChange={(val) => setCustomerId(parseInt(val))}
                disabled={isSubmitting || loadingCustomers}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a customer..." />
                </SelectTrigger>
                <SelectContent>
                  {loadingCustomers ? (
                    <div className="flex items-center justify-center p-2">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Loading customers...
                    </div>
                  ) : customers.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">
                      No customers found. Create one using the button below.
                    </div>
                  ) : (
                    customers
                      .sort((a, b) => {
                        // Sort by company name, with auto-selected customer first
                        if (a.id === defaultCustomerId) return -1;
                        if (b.id === defaultCustomerId) return 1;
                        const nameA = (a.companyName || a.contactPerson || '').toLowerCase();
                        const nameB = (b.companyName || b.contactPerson || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map((customer) => (
                        <SelectItem
                          key={customer.id}
                          value={customer.id.toString()}
                          className={customer.id === defaultCustomerId ? 'bg-green-50' : ''}
                        >
                          {customer.companyName || customer.contactPerson} - {customer.city || 'N/A'}
                          {customer.id === defaultCustomerId && ' ⭐'}
                        </SelectItem>
                      ))
                  )}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                onClick={handleCreateNewCustomer}
                disabled={isSubmitting}
                className="flex items-center gap-2 whitespace-nowrap"
              >
                <UserPlus className="h-4 w-4" />
                <span className="hidden sm:inline">New Customer</span>
              </Button>
            </div>
          </div>

          {/* Batch Selection */}
          <BatchSelector
            onSelectionsChange={handleSelectionsChange}
            disabled={isSubmitting}
          />

          {/* Delivery Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Delivery Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Expected Delivery Date */}
              <div className="space-y-2">
                <Label>Expected Delivery Date *</Label>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 font-medium">
                    ✓ Delivery will be in 8-12 working days
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deliveryAddress">Delivery Address *</Label>
                <Textarea
                  id="deliveryAddress"
                  placeholder="Enter complete delivery address..."
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  disabled={isSubmitting}
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="specialInstructions">Special Instructions</Label>
                <Textarea
                  id="specialInstructions"
                  placeholder="Any special handling or delivery instructions..."
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  disabled={isSubmitting}
                  rows={2}
                  maxLength={500}
                />
                <p className="text-xs text-muted-foreground text-right">
                  {specialInstructions.length}/500
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Order Summary */}
          {batchSelections.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span>₹{orderTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">CGST (9%):</span>
                    <span>₹{cgst.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">SGST (9%):</span>
                    <span>₹{sgst.toFixed(2)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total Amount:</span>
                    <span>₹{finalAmount.toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || batchSelections.length === 0 || !customerId}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Order...
              </>
            ) : (
              'Create Order'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateBatchOrderDialog;
